import { useRef, useState } from 'react'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import type { TimelineElementType } from '../../context/TimelineContext'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { ButtonGroup } from '../Common/ButtonGroup'
import { FloatingMenu } from '../Common/FloatingMenu'
import { AwaitingApprovalPanel } from '../ChecklistProof/AwaitingApprovalPanel'
import { elementIcon } from '../BoardExplorer'
import { useManageActionsBarStore, type ManageActionsBarProps } from './manageActionsBarStore'
import { GoogleSheetPickerButton, type SelectedSheet } from '../LinkHub/shared/pickers/GoogleSheetPickerButton'
import { exportChecklistToGoogleSheet, type SheetExportRow } from '../../services/googleSheetsService'
import { useGlobalConnectionStatus } from '../../hooks/useCheckConnection'
import { isOperationalConnectionStatus } from '../../types/integration'
import { useTimeline } from '../../context/useTimeline'
import { useWorkspace } from '../../context/useWorkspace'
import { useToast } from '../../context/useToast'

const newElementTitle = (type: TimelineElementType) =>
  type === 'GOAL' ? 'New goal' : type === 'TASK' ? 'New task' : 'New subtask'

export const ManageActionsBar = (directProps: ManageActionsBarProps) => {
  const storeProps = useManageActionsBarStore()
  const props = directProps.canEditBoard !== undefined || directProps.onCreateElement !== undefined
    ? directProps
    : (storeProps ?? directProps)

  const {
    canEditBoard = false,
    openElement = null,
    onCreateElement,
    onCreateChecklist,
    onCreateRecurringChecklist,
    clipboardItems = [],
    saveItems = [],
    discardItems = [],
    headerControls,
  } = props

  const newFolderAnchorRef = useRef<HTMLDivElement | null>(null)
  const [isNewFolderMenuOpen, setIsNewFolderMenuOpen] = useState(false)

  const { elements } = useTimeline()
  const { activeWorkspaceId } = useWorkspace()
  const { showToast } = useToast()
  const { byProvider } = useGlobalConnectionStatus()
  const sheetsConnection = byProvider['GOOGLE_SHEETS']
  const isSheetsActive = Boolean(sheetsConnection && isOperationalConnectionStatus(sheetsConnection.status))

  const handleExportBoardToSheets = async (sheet: SelectedSheet) => {
    if (!sheetsConnection || !activeWorkspaceId) return
    try {
      const rows: SheetExportRow[] = elements.map((el) => {
        let dueDate = ''
        if (el.endDate) {
          dueDate = /^\d{4}-\d{2}-\d{2}/.test(el.endDate)
            ? el.endDate.slice(0, 10)
            : (!isNaN(new Date(el.endDate).getTime()) ? new Date(el.endDate).toISOString().slice(0, 10) : '')
        }
        return {
          title: el.title || 'Untitled',
          status: (el.progress ?? 0) >= 100 ? 'COMPLETED' : 'TODO',
          notes: el.description || '',
          dueDate,
        }
      })
      const result = await exportChecklistToGoogleSheet({
        workspaceId: activeWorkspaceId,
        connectionId: sheetsConnection.id,
        title: 'Board Tasks Export',
        rows,
        spreadsheetId: sheet.id,
      })
      showToast('Successfully exported board tasks to Google Sheet.', 'success')
      if (result.spreadsheetUrl) {
        window.open(result.spreadsheetUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not export to Google Sheet.', 'error')
    }
  }

  return (
    <div className="manageActionsBar boardExplorerActions boardExplorerActions--icons" data-testid="manage-actions-bar">
      <AwaitingApprovalPanel />
      {canEditBoard && (
        <>
          {openElement ? (
            <Button
              variant="secondary"
              size="sm"
              className="boardExplorerActionLabel"
              onClick={() => onCreateElement?.()}
            >
              New folder
            </Button>
          ) : (
            <div ref={newFolderAnchorRef} style={{ position: 'relative', display: 'inline-flex' }}>
              <Button
                variant="secondary"
                size="sm"
                className="boardExplorerActionLabel"
                onClick={() => setIsNewFolderMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={isNewFolderMenuOpen}
              >
                New folder <ExpandMoreRoundedIcon fontSize="inherit" style={{ verticalAlign: '-2px' }} />
              </Button>
              <FloatingMenu
                anchorRef={newFolderAnchorRef}
                isOpen={isNewFolderMenuOpen}
                onClose={() => setIsNewFolderMenuOpen(false)}
                placement="bottom-start"
                className="breadcrumbDropdown"
                role="menu"
                ariaLabel="Choose folder type"
              >
                {(['GOAL', 'TASK', 'SUBTASK'] as TimelineElementType[]).map((type) => (
                  <Pressable
                    key={type}
                    type="button"
                    onClick={() => {
                      setIsNewFolderMenuOpen(false)
                      onCreateElement?.(type)
                    }}
                  >
                    {elementIcon(type)}
                    <span>{newElementTitle(type)}</span>
                  </Pressable>
                ))}
              </FloatingMenu>
            </div>
          )}
          <Button
            variant="primary"
            size="sm"
            className="boardExplorerActionLabel"
            onClick={() => onCreateChecklist?.()}
          >
            New file
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="boardExplorerActionLabel"
            onClick={() => onCreateRecurringChecklist?.()}
            title="Create a recurring checklist template"
          >
            <RepeatRoundedIcon fontSize="inherit" style={{ marginRight: 6, verticalAlign: 'middle' }} />
            New recurring
          </Button>

          {isSheetsActive && (
            <GoogleSheetPickerButton
              connectionId={sheetsConnection.id}
              onSheetSelected={handleExportBoardToSheets}
              onError={(err) => showToast(err.message, 'error')}
            />
          )}

          {clipboardItems.length > 0 && (
            <ButtonGroup ariaLabel="Clipboard and delete" items={clipboardItems} />
          )}
          {saveItems.length > 0 && (
            <ButtonGroup ariaLabel="Save" menuAriaLabel="Save options" collapsed items={saveItems} />
          )}
          {discardItems.length > 0 && (
            <ButtonGroup ariaLabel="Discard" menuAriaLabel="Discard options" collapsed items={discardItems} />
          )}
        </>
      )}
      {headerControls}
    </div>
  )
}

export default ManageActionsBar
